import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchPublicArticles,
  selectSearchedPublicArticles,
  selectSystemArticleTags,
  selectCommunityArticleTags,
  selectAllArticleTags,
  fetchSystemArticleTags,
  fetchCommunityArticleTags,
  fetchAllArticleTags,
  selectKeywords,
  selectPage,
  selectLastSearchKey,
  setKeywords,
  setPage,
  setLastSearchKey,
} from 'app/store/marketing/publicArticlesSlice';
import { selectIsLoading } from 'app/store/utilSlice';
import ArticlePreview from 'app/shared-components/marketing/ArticlePreview';
import EastIcon from '@mui/icons-material/East';
import Input from 'app/shared-components/marketing/Input';
import Button from 'app/shared-components/marketing/Button';
import Breadcrumbs from 'app/shared-components/marketing/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import classNames from 'classnames';
import { holdPrerenderReady, markPrerenderReady } from 'app/shared-components/util/prerenderReady';

const ARTICLES_PER_PAGE = 9;

export default function CommunityArticles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const keywords = useSelector(selectKeywords());
  const page = useSelector(selectPage());
  const lastSearchKey = useSelector(selectLastSearchKey());
  const isLoading = useSelector(selectIsLoading);

  const [systemOnly, setSystemOnly] = useState(searchParams.get('system') === 'true');
  const [communityOnly, setCommunityOnly] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  // Fetch the right tag scope based on toggles
  const systemArticleTags = useSelector(selectSystemArticleTags());
  const communityArticleTags = useSelector(selectCommunityArticleTags());
  const allArticleTags = useSelector(selectAllArticleTags());

  const activeTags = systemOnly && !communityOnly ? systemArticleTags
    : communityOnly && !systemOnly ? communityArticleTags
    : allArticleTags;

  useEffect(() => {
    if (systemOnly && !communityOnly) dispatch(fetchSystemArticleTags());
    else if (communityOnly && !systemOnly) dispatch(fetchCommunityArticleTags());
    else dispatch(fetchAllArticleTags());
  }, [systemOnly, communityOnly, dispatch]);

  const urlKeywords = searchParams.get('s') || '';

  // Sync URL keywords with slice state
  useEffect(() => {
    if (urlKeywords !== keywords) {
      dispatch(setKeywords(urlKeywords));
    }
  }, [urlKeywords, keywords, dispatch]);

  // Execute search only when criteria change
  useEffect(() => {
    const searchKey = `${keywords}-${selectedTags.join(',')}-${page}-${systemOnly}-${communityOnly}-${featuredOnly}`;
    if (searchKey !== lastSearchKey) {
      // #1320 — `keywords` (Redux, synced from the URL by the effect above) lags
      // the URL by one render on a fresh load with ?s=<term>: THIS effect fires
      // once with the still-stale keywords ('') before the sync effect's dispatch
      // flows through and re-fires it with the correct value — two real search
      // dispatches, only the second one right. Only signal readiness on the
      // SETTLED search (keywords caught up to the URL); otherwise whichever of
      // the two fetches happens to settle first can flip prerenderReady=true —
      // including the wrong one, while the correct filtered search is still in
      // flight (markPrerenderReady's guard has no per-fetch identity, so it can't
      // tell "settled" from "superseded" on its own — found by review). The
      // search itself still fires unconditionally either way — unrelated
      // pre-existing behavior, not this fix's concern.
      const isSettledSearch = keywords === urlKeywords;
      if (isSettledSearch) holdPrerenderReady();
      const searchPromise = dispatch(searchPublicArticles({
        keywords: keywords,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        featured: featuredOnly || undefined,
        systemArticles: systemOnly && !communityOnly ? true : undefined,
        communityOnly: communityOnly && !systemOnly ? true : undefined,
        limit: ARTICLES_PER_PAGE,
        offset: ARTICLES_PER_PAGE * (page - 1),
      }));
      if (isSettledSearch) searchPromise.finally(markPrerenderReady);
      dispatch(setLastSearchKey(searchKey));
    }
  }, [keywords, urlKeywords, selectedTags.join(','), page, systemOnly, communityOnly, featuredOnly, lastSearchKey, dispatch]);

  const searchedArticles = useSelector(selectSearchedPublicArticles());

  const onSearchSubmit = (e) => {
    e.preventDefault();
    
    const data = new FormData(e.target);
    const newKeywords = data.get('s');
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      s: newKeywords
    });
  }

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    dispatch(setPage(1));
  };

  const toggleSystemOnly = () => {
    setSystemOnly(v => !v);
    setSelectedTags([]);
    dispatch(setPage(1));
  };

  const toggleCommunityOnly = () => {
    setCommunityOnly(v => !v);
    setSelectedTags([]);
    dispatch(setPage(1));
  };

  const toggleFeaturedOnly = () => {
    setFeaturedOnly(v => !v);
    dispatch(setPage(1));
  };

  const showMore = () => {
    dispatch(setPage(page + 1));
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <section>
        <div className="container py-28">
          <Breadcrumbs
            trail={[
              { label: 'Home', to: '/' },
              { label: 'Blog', to: '/blog' },
              { label: 'Community Articles', to: '/community-articles' }
            ]}
          />
        </div>
      </section>

      {/* Articles */}
      <section>
        <div className="container pb-80">
          <h1 className="text-48 text-hs-grey-black leading-none font-medium">
            Community Articles
          </h1>

          <div className="flex flex-col gap-20 mt-40">
            <form onSubmit={onSearchSubmit}>
              <Input
                placeholder="Search Community Articles"
                name="s"
                defaultValue={urlKeywords}
              />
            </form>

            <div className="flex flex-wrap gap-8">
              <button
                className={classNames([
                  'text-12 border-2 border-hs-blue px-10 py-4 font-medium rounded-full',
                  systemOnly ? 'text-hs-white bg-hs-blue' : 'text-hs-blue bg-hs-white'
                ])}
                onClick={toggleSystemOnly}
              >
                Halespring
              </button>
              <button
                className={classNames([
                  'text-12 border-2 border-hs-blue px-10 py-4 font-medium rounded-full',
                  communityOnly ? 'text-hs-white bg-hs-blue' : 'text-hs-blue bg-hs-white'
                ])}
                onClick={toggleCommunityOnly}
              >
                Community
              </button>
              <button
                className={classNames([
                  'text-12 border-2 border-hs-blue px-10 py-4 font-medium rounded-full',
                  featuredOnly ? 'text-hs-white bg-hs-blue' : 'text-hs-blue bg-hs-white'
                ])}
                onClick={toggleFeaturedOnly}
              >
                Featured
              </button>
            </div>

            {activeTags?.length > 0 && (
              <div className="flex flex-wrap gap-8">
                {activeTags.map((tag) => (
                  <button
                    key={tag}
                    className={classNames([
                      'block text-12 border-2 border-hs-blue px-10 py-4 font-medium rounded-full',
                      selectedTags.includes(tag) ? 'text-hs-white bg-hs-blue' : 'text-hs-blue bg-hs-white'
                    ])}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-32 mt-40">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-20 mt-40">
              {searchedArticles?.map((article) => (
                <ArticlePreview key={article.id} article={article} />
              ))}
            </div>
          )}

          <div className="mt-40">
            {searchedArticles?.length === ARTICLES_PER_PAGE * page && (
              <Button
                className="text-hs-blue outline-hs-blue"
                onClick={showMore}
              >
                Show More <EastIcon className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}